import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Download,
  Eye,
  ExternalLink,
  Globe,
  Loader2,
  Pencil,
  Plus,
  ScrollText,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { api, type Competition, type OrgPublicPageContent, type OrgPublicPageResponse } from '../lib/api';
import { Card } from './Card';
import { CollapsibleSection } from './CollapsibleSection';
import { CopyLinkButton } from './CopyLinkButton';
import { FocalPointPicker } from './FocalPointPicker';
import { ImageDropzone } from './ImageDropzone';
import { OrgPublicPageView } from './OrgPublicPageView';
import { SaveBar } from './SaveBar';
import { cn } from '../lib/cn';
import { SETTLE, settleDelay } from '../lib/motion';

// ── Local row types (role/discord are kept as strings internally; converted to
//    optional on save) ──────────────────────────────────────────────────────────

type Social      = { label: string; url: string };
type Partner     = { logoAssetUrl?: string; logoObjectPosition?: string; name: string; url: string };
type Testimonial = { quote: string; author: string; role: string };
type FaqItem     = { q: string; a: string };
type StaffMember = { name: string; role: string; discord: string };

// Season Results — a season is a title + ranked divisions of teams. `place` is
// NOT tracked as separate editable state; it's derived from array order at
// save time (index + 1), so reordering via the move up/down buttons IS the
// only way to change a team's finishing place — there's no separate numeric
// field that could drift out of sync with the row order.
type SeasonTeam     = { clanName: string; displayName: string; logoUrl?: string; score: number };
type SeasonDivision = { tier: string; teams: SeasonTeam[] };
type SeasonRow      = { title: string; divisions: SeasonDivision[] };

// Human-friendly label for a division tier ('PRO_AMS_DIV_1' → 'Pro Ams Div 1').
// Mirrors Standings.tsx/Watch.tsx's local tierLabel (not exported there).
function tierLabel(tier: string) {
  if (tier === 'UNASSIGNED') return 'Unassigned';
  return tier.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

interface FormState {
  published: boolean;
  tagline: string;
  whoWeAre: string;
  howWeWork: string;
  email: string;
  discordInviteUrl: string;
  socials: Social[];
  contactNote: string;
  partners: Partner[];
  testimonials: Testimonial[];
  faq: FaqItem[];
  staff: StaffMember[];
  rules: string;
  featuredCompId: string;
  seasons: SeasonRow[];
  // Image fields preserved from backend — no input UI this round
  heroLogoAssetUrl?: string;
  heroLogoObjectPosition?: string;
  heroBannerAssetUrl?: string;
  heroBannerObjectPosition?: string;
}

const EMPTY: FormState = {
  published: false,
  tagline: '',
  whoWeAre: '',
  howWeWork: '',
  email: '',
  discordInviteUrl: '',
  socials: [],
  contactNote: '',
  partners: [],
  testimonials: [],
  faq: [],
  staff: [],
  rules: '',
  featuredCompId: '',
  seasons: [],
};

// ── Blob ↔ form conversion ─────────────────────────────────────────────────────

function fromBlob(blob: OrgPublicPageContent): FormState {
  return {
    published:        blob.published,
    tagline:          blob.hero?.tagline ?? '',
    whoWeAre:         blob.about?.whoWeAre ?? '',
    howWeWork:        blob.about?.howWeWork ?? '',
    email:            blob.contact?.email ?? '',
    discordInviteUrl: blob.contact?.discordInviteUrl ?? '',
    socials:          (blob.contact?.socials ?? []).map(s => ({ label: s.label, url: s.url })),
    contactNote:      blob.contact?.note ?? '',
    partners:         (blob.partners ?? []).map(p => ({
      logoAssetUrl: p.logoAssetUrl,
      logoObjectPosition: p.logoObjectPosition,
      name:         p.name,
      url:          p.url ?? '',
    })),
    testimonials:     (blob.testimonials ?? []).map(t => ({
      quote:  t.quote,
      author: t.author,
      role:   t.role ?? '',
    })),
    faq:   (blob.faq ?? []).map(f => ({ q: f.q, a: f.a })),
    staff: (blob.staff ?? []).map(s => ({
      name:    s.name,
      role:    s.role ?? '',
      discord: s.discord ?? '',
    })),
    rules:            blob.rules ?? '',
    featuredCompId:   blob.featuredCompId ?? '',
    seasons: (blob.seasons ?? []).map(s => ({
      title: s.title,
      divisions: (s.divisions ?? []).map(d => ({
        tier: d.tier,
        teams: (d.teams ?? []).map(t => ({
          clanName:    t.clanName,
          displayName: t.displayName ?? '',
          logoUrl:     t.logoUrl,
          score:       t.score ?? 0,
        })),
      })),
    })),
    heroLogoAssetUrl: blob.hero?.logoAssetUrl,
    heroLogoObjectPosition: blob.hero?.logoObjectPosition,
    heroBannerAssetUrl: blob.hero?.bannerAssetUrl,
    heroBannerObjectPosition: blob.hero?.bannerObjectPosition,
  };
}

function toBlob(form: FormState): OrgPublicPageContent {
  // Hero — preserve existing image urls; include tagline if set
  const heroFields: OrgPublicPageContent['hero'] = {};
  if (form.heroLogoAssetUrl)   heroFields.logoAssetUrl   = form.heroLogoAssetUrl;
  if (form.heroLogoAssetUrl && form.heroLogoObjectPosition)     heroFields.logoObjectPosition   = form.heroLogoObjectPosition;
  if (form.heroBannerAssetUrl) heroFields.bannerAssetUrl = form.heroBannerAssetUrl;
  if (form.heroBannerAssetUrl && form.heroBannerObjectPosition) heroFields.bannerObjectPosition = form.heroBannerObjectPosition;
  if (form.tagline.trim())     heroFields.tagline        = form.tagline.trim();
  const hero = Object.keys(heroFields).length > 0 ? heroFields : undefined;

  // About
  const aboutFields: OrgPublicPageContent['about'] = {};
  if (form.whoWeAre.trim()) aboutFields.whoWeAre = form.whoWeAre;
  if (form.howWeWork.trim()) aboutFields.howWeWork = form.howWeWork;
  const about = Object.keys(aboutFields).length > 0 ? aboutFields : undefined;

  // Contact
  const contactFields: OrgPublicPageContent['contact'] = {};
  if (form.email.trim())            contactFields.email            = form.email.trim();
  if (form.discordInviteUrl.trim()) contactFields.discordInviteUrl = form.discordInviteUrl.trim();
  if (form.contactNote.trim())      contactFields.note             = form.contactNote.trim();
  const validSocials = form.socials.filter(s => s.label.trim() || s.url.trim());
  if (validSocials.length > 0)
    contactFields.socials = validSocials.map(s => ({ label: s.label.trim(), url: s.url.trim() }));
  const contact = Object.keys(contactFields).length > 0 ? contactFields : undefined;

  // Partners — preserve per-row logoAssetUrl if present
  const validPartners = form.partners.filter(p => p.name.trim());
  const partners = validPartners.length > 0
    ? validPartners.map(p => ({
        ...(p.logoAssetUrl ? { logoAssetUrl: p.logoAssetUrl } : {}),
        ...(p.logoAssetUrl && p.logoObjectPosition ? { logoObjectPosition: p.logoObjectPosition } : {}),
        name: p.name.trim(),
        ...(p.url.trim() ? { url: p.url.trim() } : {}),
      }))
    : undefined;

  // Testimonials
  const validTestimonials = form.testimonials.filter(t => t.quote.trim() && t.author.trim());
  const testimonials = validTestimonials.length > 0
    ? validTestimonials.map(t => ({
        quote:  t.quote,
        author: t.author,
        ...(t.role.trim() ? { role: t.role.trim() } : {}),
      }))
    : undefined;

  // FAQ
  const validFaq = form.faq.filter(f => f.q.trim() && f.a.trim());
  const faq = validFaq.length > 0 ? validFaq : undefined;

  // Staff
  const validStaff = form.staff.filter(s => s.name.trim());
  const staff = validStaff.length > 0
    ? validStaff.map(s => ({
        name: s.name.trim(),
        ...(s.role.trim()    ? { role:    s.role.trim()    } : {}),
        ...(s.discord.trim() ? { discord: s.discord.trim() } : {}),
      }))
    : undefined;

  // Seasons — drop empty divisions/teams; `place` is derived from row order
  // (index + 1), never carried as separate editable state.
  const validSeasons = form.seasons.filter(s => s.title.trim());
  const seasons = validSeasons.length > 0
    ? validSeasons.map(s => {
        const divisions = s.divisions
          .map(d => {
            const teams = d.teams
              .filter(t => t.clanName.trim())
              .map((t, i) => ({
                clanName: t.clanName.trim(),
                ...(t.displayName.trim() ? { displayName: t.displayName.trim() } : {}),
                ...(t.logoUrl ? { logoUrl: t.logoUrl } : {}),
                ...(Number.isFinite(t.score) ? { score: t.score } : {}),
                place: i + 1,
              }));
            return { tier: d.tier.trim(), teams };
          })
          .filter(d => d.tier && d.teams.length > 0);
        return { title: s.title.trim(), divisions };
      })
    : undefined;

  return {
    published: form.published,
    ...(hero          ? { hero }          : {}),
    ...(about         ? { about }         : {}),
    ...(contact       ? { contact }       : {}),
    ...(partners      ? { partners }      : {}),
    ...(testimonials  ? { testimonials }  : {}),
    ...(faq           ? { faq }           : {}),
    ...(staff         ? { staff }         : {}),
    ...(form.rules.trim() ? { rules: form.rules.trim() } : {}),
    ...(form.featuredCompId ? { featuredCompId: form.featuredCompId } : {}),
    ...(seasons       ? { seasons }       : {}),
  };
}

// ── Small layout helpers ───────────────────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <h3 className="mb-3 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
      {label}
    </h3>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-500">
      {children}
    </p>
  );
}

function AddRowButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-2 flex items-center gap-1.5 border border-iron-800 px-3 py-1.5 font-display text-[9px] font-black uppercase tracking-wider text-iron-400 transition-colors hover:border-forge-500 hover:text-forge-200"
    >
      <Plus className="h-3 w-3" strokeWidth={2.5} />
      {label}
    </button>
  );
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-8 w-8 shrink-0 items-center justify-center border border-iron-800 text-iron-500 transition-colors hover:border-red-700 hover:text-red-400"
      aria-label="Remove row"
    >
      <Trash2 className="h-3.5 w-3.5" strokeWidth={2.5} />
    </button>
  );
}

// ── Image upload control ───────────────────────────────────────────────────────
// Handles: file pick → presigned PUT → calls onUploaded(publicUrl) on success.
// Shows a thumbnail of the current url, an upload button, and a clear button.

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB — mirrors backend ASSET_MAX_FILE_SIZE

function ImageUploadControl({
  orgId,
  currentUrl,
  label,
  onUploaded,
  onClear,
}: {
  orgId: string;
  currentUrl?: string;
  label: string;
  onUploaded: (publicUrl: string) => void;
  onClear: () => void;
}) {
  const inputRef  = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError(`Unsupported type. Use PNG, JPEG, WebP, or SVG.`);
      return;
    }
    if (file.size === 0 || file.size > MAX_FILE_BYTES) {
      setError(`File must be 1 byte – 5 MB.`);
      return;
    }

    setLoading(true);
    try {
      const { uploadUrl, publicUrl } = await api.orgPublicPageUploadUrl(orgId, file.type, file.size);
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      onUploaded(publicUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-1.5">
      <FieldLabel>{label}</FieldLabel>

      {currentUrl ? (
        <div className="flex items-start gap-2">
          <img
            src={currentUrl}
            alt={label}
            className="h-14 w-auto max-w-[10rem] border border-iron-800 object-contain bg-iron-950"
          />
          <button
            type="button"
            onClick={onClear}
            className="flex h-7 w-7 shrink-0 items-center justify-center border border-iron-800 text-iron-500 transition-colors hover:border-red-700 hover:text-red-400"
            aria-label={`Remove ${label}`}
          >
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>
      ) : (
        <p className="text-[10px] italic text-iron-500">No image set.</p>
      )}

      {/* Wrap mode — drop anywhere on the button hands the file to the same
          handleFile the click path uses; no new upload logic. */}
      <ImageDropzone onFile={file => void handleFile(file)} accept={ALLOWED_IMAGE_TYPES.join(',')} disabled={loading}>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_IMAGE_TYPES.join(',')}
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) { void handleFile(file); }
              e.target.value = '';
            }}
          />
          <button
            type="button"
            disabled={loading}
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-1.5 border border-dashed border-iron-800 px-3 py-1.5 font-display text-[9px] font-black uppercase tracking-wider text-iron-400 transition-colors hover:border-forge-500 hover:text-forge-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading
              ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
              : <Upload className="h-3 w-3" strokeWidth={2.5} />
            }
            {loading ? 'Uploading…' : (currentUrl ? 'Replace or drag a new image here' : 'Upload or drag an image here')}
          </button>
        </div>
      </ImageDropzone>

      {error && (
        <div className="flex items-center gap-1.5 text-[10px] text-red-400">
          <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          {error}
        </div>
      )}
    </div>
  );
}

// ── Season import control ──────────────────────────────────────────────────────
// The "auto image" mechanism: picks a competition, loads its divisions via
// api.divisions(compId), and turns each division's `standings` rows straight
// into ranked SeasonDivision teams — logoUrl comes verbatim off the standings
// row (DivisionTeamScore.logoUrl), never a separate upload. Imported divisions
// are APPENDED to the season (not a destructive overwrite) so re-importing, or
// importing from a second competition, doesn't silently wipe manual edits.

function SeasonImportControl({
  comps,
  onImport,
}: {
  comps: Competition[];
  onImport: (divisions: SeasonDivision[]) => void;
}) {
  const [compId, setCompId]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  async function handleImport() {
    if (!compId) return;
    setLoading(true);
    setError(null);
    try {
      const divisions = await api.divisions(compId);
      const mapped: SeasonDivision[] = divisions
        .filter(d => (d.standings?.length ?? 0) > 0)
        .map(d => ({
          tier: tierLabel(d.tier),
          teams: (d.standings ?? []).map(s => ({
            clanName:    s.clanName,
            displayName: s.displayName ?? '',
            logoUrl:     s.logoUrl,
            score:       s.total ?? 0,
          })),
        }));
      if (mapped.length === 0) {
        setError('That competition has no scored divisions to import yet.');
        return;
      }
      onImport(mapped);
      setCompId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-1.5 border border-iron-800/60 bg-iron-950 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="input flex-1 text-sm"
          value={compId}
          onChange={e => setCompId(e.target.value)}
        >
          <option value="">Import from a competition…</option>
          {comps.map(c => (
            <option key={c.id} value={c.id}>{c.name || c.id.replace('COMP#', '')}</option>
          ))}
        </select>
        <button
          type="button"
          disabled={!compId || loading}
          onClick={() => { void handleImport(); }}
          className="flex items-center gap-1.5 border border-iron-800 px-3 py-1.5 font-display text-[9px] font-black uppercase tracking-wider text-iron-400 transition-colors hover:border-forge-500 hover:text-forge-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading
            ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
            : <Download className="h-3 w-3" strokeWidth={2.5} />
          }
          {loading ? 'Importing…' : 'Import divisions'}
        </button>
      </div>
      {error && (
        <div className="flex items-center gap-1.5 text-[10px] text-red-400">
          <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          {error}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

// Empty defaults for the derived (data-driven) sections of the preview when the
// org page isn't published yet — publicOrgPage 404s for a draft, so there's no
// real comps/stats/history to show. The editable content (hero/about/contact/
// socials/partners/testimonials/faq/staff) still previews fully; these sections
// simply don't render (they're already conditionally rendered in the view).
const EMPTY_PREVIEW_STATS = { totalComps: 0, totalTeams: 0, totalPlayers: 0, yearsActive: 0 };

export function OrgPublicPageEditor({
  orgId,
  slug,
  orgName,
  accentColor,
  createdAt,
}: {
  orgId: string;
  slug?: string;
  orgName?: string;
  accentColor?: string | null;
  createdAt?: number | null;
}) {
  const qc = useQueryClient();

  // ── ALL hooks before any early return ────────────────────────────────────────
  const [form, setForm]   = useState<FormState>(EMPTY);
  // Snapshot of the last-loaded/last-saved form — the dirty-tracking baseline
  // the sticky SaveBar reads (Lisa, 2026-07-19: save affordance must be
  // obvious and live at the bottom, not buried mid-form). Also doubles as
  // the Discard target — no parallel state invented beyond this one snapshot.
  const [savedSnapshot, setSavedSnapshot] = useState<FormState>(EMPTY);
  const [saved, setSaved] = useState(false);
  const [mode,  setMode]  = useState<'edit' | 'preview'>('edit');

  const { data: draft, isLoading: draftLoading } = useQuery({
    queryKey: ['orgPublicPage', orgId],
    queryFn: () => api.getOrgPublicPage(orgId),
  });

  const { data: orgComps = [] } = useQuery({
    queryKey: ['orgCompetitions', orgId],
    queryFn: () => api.orgCompetitions(orgId),
  });

  // Season Results import picker — same source Divisions.tsx uses for its own
  // competition switcher (api.competitions(), not the org-scoped list above;
  // the divisions endpoint itself is the real access gate).
  const { data: allComps = [] } = useQuery({
    queryKey: ['competitions'],
    queryFn: api.competitions,
  });

  // Derived (data-driven) sections for the live preview — real fetch of the
  // published page when one exists (member + published → 200), null otherwise
  // (unpublished draft → 404, caught rather than surfaced as a query error).
  // Only runs while the Preview tab is actually open.
  const { data: previewDerived } = useQuery({
    queryKey: ['orgPreviewDerived', slug],
    queryFn: () => api.publicOrgPage(slug!).catch(() => null),
    enabled: mode === 'preview' && !!slug,
  });

  // Initialise form from loaded draft (fires once when data arrives) — also
  // resets the dirty-tracking snapshot to match, so a background refetch
  // (e.g. right after a save) never leaves the form looking falsely dirty.
  useEffect(() => {
    if (!draft) return;
    const next = fromBlob(draft);
    setForm(next);
    setSavedSnapshot(next);
  }, [draft]);

  const saveMut = useMutation({
    mutationFn: (blob: OrgPublicPageContent) => api.putOrgPublicPage(orgId, blob),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      qc.invalidateQueries({ queryKey: ['orgPublicPage', orgId] });
    },
  });

  const dirty = JSON.stringify(form) !== JSON.stringify(savedSnapshot);
  // ── End of hooks zone ─────────────────────────────────────────────────────────

  if (draftLoading) {
    return (
      <Card>
        <div className="flex items-center gap-2 py-6 text-iron-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-xs">Loading public page settings…</span>
        </div>
      </Card>
    );
  }

  // ── Derived values ────────────────────────────────────────────────────────────
  const publicUrl  = slug ? `${window.location.origin}/org/${slug}` : null;
  const publicPath = slug ? `/org/${slug}` : null;

  // Live WYSIWYG preview data — assembled from the in-progress form state (no
  // save) plus whatever derived data is available. When the page isn't
  // published, publicOrgPage 404s and previewDerived is null — fall back to
  // empty defaults so the preview still shows every editable section, just
  // without the data-driven ones (they're already conditionally rendered).
  const previewData: OrgPublicPageResponse = {
    org: {
      name: orgName ?? slug ?? 'Your Org',
      slug: slug ?? '',
      accentColor: accentColor ?? null,
      createdAt: createdAt ?? null,
    },
    page: toBlob(form),
    comps: previewDerived?.comps ?? [],
    stats: previewDerived?.stats ?? EMPTY_PREVIEW_STATS,
    history: previewDerived?.history ?? [],
    hallOfChampions: previewDerived?.hallOfChampions ?? [],
    memberClans: previewDerived?.memberClans ?? [],
    ...(previewDerived?.featuredComp ? { featuredComp: previewDerived.featuredComp } : {}),
  };
  const previewIsUnpublished = mode === 'preview' && !previewDerived;

  // ── Form helpers (not hooks — safe after early return) ─────────────────────

  function patch(update: Partial<FormState>) {
    setForm(prev => ({ ...prev, ...update }));
  }

  // Inline row update helpers — typed per-array to keep TS happy

  function updateSocial(idx: number, update: Partial<Social>) {
    const next = form.socials.map((s, i) => i === idx ? { ...s, ...update } : s);
    patch({ socials: next });
  }

  function updatePartner(idx: number, update: Partial<Partner>) {
    const next = form.partners.map((p, i) => i === idx ? { ...p, ...update } : p);
    patch({ partners: next });
  }

  function updateTestimonial(idx: number, update: Partial<Testimonial>) {
    const next = form.testimonials.map((t, i) => i === idx ? { ...t, ...update } : t);
    patch({ testimonials: next });
  }

  function updateFaq(idx: number, update: Partial<FaqItem>) {
    const next = form.faq.map((f, i) => i === idx ? { ...f, ...update } : f);
    patch({ faq: next });
  }

  function updateStaff(idx: number, update: Partial<StaffMember>) {
    const next = form.staff.map((s, i) => i === idx ? { ...s, ...update } : s);
    patch({ staff: next });
  }

  function removeAt<T>(arr: T[], idx: number): T[] {
    return arr.filter((_, i) => i !== idx);
  }

  // ── Season Results — nested season → division → team updates ───────────────

  function updateSeason(si: number, update: Partial<SeasonRow>) {
    patch({ seasons: form.seasons.map((s, i) => i === si ? { ...s, ...update } : s) });
  }

  function updateDivision(si: number, di: number, update: Partial<SeasonDivision>) {
    patch({
      seasons: form.seasons.map((s, i) => i !== si ? s : {
        ...s,
        divisions: s.divisions.map((d, j) => j === di ? { ...d, ...update } : d),
      }),
    });
  }

  function updateTeam(si: number, di: number, ti: number, update: Partial<SeasonTeam>) {
    patch({
      seasons: form.seasons.map((s, i) => i !== si ? s : {
        ...s,
        divisions: s.divisions.map((d, j) => j !== di ? d : {
          ...d,
          teams: d.teams.map((t, k) => k === ti ? { ...t, ...update } : t),
        }),
      }),
    });
  }

  function moveTeam(si: number, di: number, ti: number, dir: -1 | 1) {
    const division = form.seasons[si]?.divisions[di];
    if (!division) return;
    const newIdx = ti + dir;
    if (newIdx < 0 || newIdx >= division.teams.length) return;
    const teams = [...division.teams];
    [teams[ti], teams[newIdx]] = [teams[newIdx], teams[ti]];
    updateDivision(si, di, { teams });
  }

  function addSeason() {
    patch({ seasons: [...form.seasons, { title: '', divisions: [] }] });
  }

  function removeSeason(si: number) {
    patch({ seasons: removeAt(form.seasons, si) });
  }

  function addDivision(si: number) {
    updateSeason(si, { divisions: [...form.seasons[si].divisions, { tier: '', teams: [] }] });
  }

  function removeDivision(si: number, di: number) {
    updateSeason(si, { divisions: removeAt(form.seasons[si].divisions, di) });
  }

  function addTeam(si: number, di: number) {
    updateDivision(si, di, {
      teams: [...form.seasons[si].divisions[di].teams, { clanName: '', displayName: '', score: 0 }],
    });
  }

  function removeTeam(si: number, di: number, ti: number) {
    updateDivision(si, di, { teams: removeAt(form.seasons[si].divisions[di].teams, ti) });
  }

  function importDivisions(si: number, imported: SeasonDivision[]) {
    updateSeason(si, { divisions: [...form.seasons[si].divisions, ...imported] });
  }

  const handleSave = () => {
    setSaved(false);
    const snapshot = form;
    saveMut.mutate(toBlob(snapshot), {
      onSuccess: () => setSavedSnapshot(snapshot),
    });
  };

  // One Save writes the whole page blob (form state is shared across the
  // Public Page and Rules segments), so the same bar is mounted in both.
  const saveBar = (
    <SaveBar
      dirty={dirty}
      saving={saveMut.isPending}
      saved={saved}
      error={saveMut.isError ? (saveMut.error as Error).message : null}
      onSave={handleSave}
      onDiscard={() => setForm(savedSnapshot)}
    />
  );

  // ── JSX ───────────────────────────────────────────────────────────────────────

  return (
    <div className={`${SETTLE} space-y-4`} style={settleDelay(0)}>

      <CollapsibleSection
        title="Public Page"
        hint="Publish settings and everything visitors see on the org's public page."
        icon={Globe}
      >
      <div className="space-y-4">

      {/* ── Edit ⇄ Preview toggle — live WYSIWYG, no save, no new tab ── */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
          Public Page Editor
        </h2>
        <div className="inline-flex border border-iron-800">
          <button
            type="button"
            onClick={() => setMode('edit')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-colors',
              mode === 'edit'
                ? 'bg-forge-500/15 text-forge-200'
                : 'text-iron-500 hover:text-iron-200',
            )}
          >
            <Pencil className="h-3 w-3" strokeWidth={2.5} />
            Edit
          </button>
          <button
            type="button"
            onClick={() => setMode('preview')}
            className={cn(
              'flex items-center gap-1.5 border-l border-iron-800 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-colors',
              mode === 'preview'
                ? 'bg-forge-500/15 text-forge-200'
                : 'text-iron-500 hover:text-iron-200',
            )}
          >
            <Eye className="h-3 w-3" strokeWidth={2.5} />
            Preview
          </button>
        </div>
      </div>

      {/* ── Discoverability card (publish toggle + view/copy) ── */}
      <Card>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <h2 className="flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
            <Globe className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
            Public Organisation Page
          </h2>

          {/* Publish toggle — saves IMMEDIATELY so the live page always matches
              the toggle. The optimistic flip is reverted if the save fails, so a
              green "Published" state can never lie about what is actually live.
              (Previously this was local-only state persisted on Save, which let
              users toggle green, hit "View public page", and land on an
              unpublished 404.) */}
          <button
            type="button"
            disabled={saveMut.isPending}
            onClick={() => {
              const next = !form.published;
              const patched = { ...form, published: next };
              patch({ published: next });
              saveMut.mutate(toBlob(patched), {
                onSuccess: () => setSavedSnapshot(patched),
                onError: () => patch({ published: !next }),
              });
            }}
            className={cn(
              'flex items-center gap-2 border px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-colors disabled:opacity-60',
              form.published
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                : 'border-iron-700 bg-iron-950 text-iron-400 hover:border-iron-600 hover:text-iron-200',
            )}
          >
            <span
              className={cn(
                'h-2 w-2 rounded-full',
                form.published ? 'bg-emerald-400' : 'bg-iron-600',
              )}
            />
            {saveMut.isPending
              ? 'Saving…'
              : form.published ? 'Published — live' : 'Draft — not public'}
          </button>
        </div>

        {/* View + copy */}
        {publicUrl && publicPath ? (
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={publicPath}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 border border-iron-800 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-forge-500 hover:text-forge-200"
            >
              <ExternalLink className="h-3 w-3" strokeWidth={2.5} />
              View public page
            </a>
            <CopyLinkButton
              url={publicUrl}
              label="Copy link"
              className="flex items-center gap-1.5 border border-iron-800 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-forge-500 hover:text-forge-200"
            />
            {!form.published && (
              <span className="text-[10px] text-iron-500">
                Page exists but won't be visible to others until published.
              </span>
            )}
          </div>
        ) : (
          <p className="text-[11px] text-iron-500">
            Org slug not available — public URL will appear here once configured.
          </p>
        )}
      </Card>

      {mode === 'preview' ? (
        <div className="space-y-3">
          {previewIsUnpublished && (
            <p className="text-[11px] italic text-iron-500">
              Competitions, teams &amp; stats appear here once your page is published.
            </p>
          )}
          <div className="border border-iron-800">
            <OrgPublicPageView data={previewData} />
          </div>
        </div>
      ) : (
      <>

      {/* ── Hero images + tagline ── */}
      <Card>
        <SectionHeader label="Hero" />
        <div className="space-y-4">
          <ImageUploadControl
            orgId={orgId}
            label="Logo"
            currentUrl={form.heroLogoAssetUrl}
            onUploaded={url => patch({ heroLogoAssetUrl: url, heroLogoObjectPosition: undefined })}
            onClear={() => patch({ heroLogoAssetUrl: undefined, heroLogoObjectPosition: undefined })}
          />
          {form.heroLogoAssetUrl && (
            <FocalPointPicker
              src={form.heroLogoAssetUrl}
              aspect={1}
              value={form.heroLogoObjectPosition}
              onChange={pos => patch({ heroLogoObjectPosition: pos })}
              label="Logo focal point"
            />
          )}
          <ImageUploadControl
            orgId={orgId}
            label="Banner"
            currentUrl={form.heroBannerAssetUrl}
            onUploaded={url => patch({ heroBannerAssetUrl: url, heroBannerObjectPosition: undefined })}
            onClear={() => patch({ heroBannerAssetUrl: undefined, heroBannerObjectPosition: undefined })}
          />
          {form.heroBannerAssetUrl && (
            <FocalPointPicker
              src={form.heroBannerAssetUrl}
              aspect={3.2}
              value={form.heroBannerObjectPosition}
              onChange={pos => patch({ heroBannerObjectPosition: pos })}
              label="Banner focal point"
            />
          )}
          <div>
            <FieldLabel>Tagline</FieldLabel>
            <input
              type="text"
              className="input w-full text-sm"
              placeholder="e.g. South Africa's premier esports organisation"
              value={form.tagline}
              onChange={e => patch({ tagline: e.target.value })}
            />
            <p className="mt-1 text-[10px] text-iron-500">
              Short punchy line shown at the top of your public page.
            </p>
          </div>
        </div>
      </Card>

      {/* ── About ── */}
      <Card>
        <SectionHeader label="About" />
        <div className="space-y-4">
          <div>
            <FieldLabel>Who we are</FieldLabel>
            <textarea
              className="input w-full resize-y text-sm"
              rows={4}
              placeholder="Your org's story, founding, mission…"
              value={form.whoWeAre}
              onChange={e => patch({ whoWeAre: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel>How we work</FieldLabel>
            <textarea
              className="input w-full resize-y text-sm"
              rows={4}
              placeholder="How you run competitions, onboard teams, your overall vibe…"
              value={form.howWeWork}
              onChange={e => patch({ howWeWork: e.target.value })}
            />
          </div>
        </div>
      </Card>

      {/* ── Contact ── */}
      <Card>
        <SectionHeader label="Contact" />
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel>Email</FieldLabel>
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="contact@yourorg.gg"
                value={form.email}
                onChange={e => patch({ email: e.target.value })}
              />
            </div>
            <div>
              <FieldLabel>Discord invite URL</FieldLabel>
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="https://discord.gg/…"
                value={form.discordInviteUrl}
                onChange={e => patch({ discordInviteUrl: e.target.value })}
              />
            </div>
          </div>

          {/* Socials — repeatable rows */}
          <div>
            <FieldLabel>Social links</FieldLabel>
            <div className="space-y-2">
              {form.socials.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 border border-iron-800/60 bg-iron-950 p-2"
                >
                  <input
                    type="text"
                    className="input flex-1 text-sm"
                    placeholder="Label (e.g. Twitter)"
                    value={s.label}
                    onChange={e => updateSocial(i, { label: e.target.value })}
                  />
                  <input
                    type="text"
                    className="input flex-[2] text-sm"
                    placeholder="URL"
                    value={s.url}
                    onChange={e => updateSocial(i, { url: e.target.value })}
                  />
                  <RemoveButton onClick={() => patch({ socials: removeAt(form.socials, i) })} />
                </div>
              ))}
              {form.socials.length === 0 && (
                <p className="text-[11px] italic text-iron-500">No social links yet.</p>
              )}
            </div>
            <AddRowButton
              label="Add social"
              onClick={() => patch({ socials: [...form.socials, { label: '', url: '' }] })}
            />
          </div>

          <div>
            <FieldLabel>Note</FieldLabel>
            <textarea
              className="input w-full resize-y text-sm"
              rows={2}
              placeholder="Anything else visitors should know about reaching you…"
              value={form.contactNote}
              onChange={e => patch({ contactNote: e.target.value })}
            />
          </div>
        </div>
      </Card>

      {/* ── Partners ── */}
      <Card>
        <SectionHeader label="Partners" />
        <div className="space-y-4">
          {form.partners.map((p, i) => (
            <div
              key={i}
              className="space-y-2 border border-iron-800/60 bg-iron-950 p-3"
            >
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  className="input flex-1 text-sm"
                  placeholder="Partner name"
                  value={p.name}
                  onChange={e => updatePartner(i, { name: e.target.value })}
                />
                <input
                  type="text"
                  className="input flex-1 text-sm"
                  placeholder="URL (optional)"
                  value={p.url}
                  onChange={e => updatePartner(i, { url: e.target.value })}
                />
                <RemoveButton onClick={() => patch({ partners: removeAt(form.partners, i) })} />
              </div>
              <ImageUploadControl
                orgId={orgId}
                label="Logo"
                currentUrl={p.logoAssetUrl}
                onUploaded={url => updatePartner(i, { logoAssetUrl: url, logoObjectPosition: undefined })}
                onClear={() => updatePartner(i, { logoAssetUrl: undefined, logoObjectPosition: undefined })}
              />
              {p.logoAssetUrl && (
                <FocalPointPicker
                  src={p.logoAssetUrl}
                  aspect={1}
                  value={p.logoObjectPosition}
                  onChange={pos => updatePartner(i, { logoObjectPosition: pos })}
                  label="Logo focal point"
                />
              )}
            </div>
          ))}
          {form.partners.length === 0 && (
            <p className="text-[11px] italic text-iron-500">No partners listed yet.</p>
          )}
        </div>
        <AddRowButton
          label="Add partner"
          onClick={() => patch({ partners: [...form.partners, { name: '', url: '' }] })}
        />
      </Card>

      {/* ── Testimonials ── */}
      <Card>
        <SectionHeader label="Testimonials" />
        <div className="space-y-2">
          {form.testimonials.map((t, i) => (
            <div
              key={i}
              className="space-y-2 border border-iron-800/60 bg-iron-950 p-3"
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-2">
                  <textarea
                    className="input w-full resize-y text-sm"
                    rows={2}
                    placeholder="Quote"
                    value={t.quote}
                    onChange={e => updateTestimonial(i, { quote: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input flex-1 text-sm"
                      placeholder="Author name"
                      value={t.author}
                      onChange={e => updateTestimonial(i, { author: e.target.value })}
                    />
                    <input
                      type="text"
                      className="input flex-1 text-sm"
                      placeholder="Role (optional)"
                      value={t.role}
                      onChange={e => updateTestimonial(i, { role: e.target.value })}
                    />
                  </div>
                </div>
                <RemoveButton
                  onClick={() => patch({ testimonials: removeAt(form.testimonials, i) })}
                />
              </div>
            </div>
          ))}
          {form.testimonials.length === 0 && (
            <p className="text-[11px] italic text-iron-500">No testimonials yet.</p>
          )}
        </div>
        <AddRowButton
          label="Add testimonial"
          onClick={() =>
            patch({ testimonials: [...form.testimonials, { quote: '', author: '', role: '' }] })
          }
        />
      </Card>

      {/* ── FAQ ── */}
      <Card>
        <SectionHeader label="FAQ" />
        <div className="space-y-2">
          {form.faq.map((f, i) => (
            <div
              key={i}
              className="space-y-2 border border-iron-800/60 bg-iron-950 p-3"
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    className="input w-full text-sm"
                    placeholder="Question"
                    value={f.q}
                    onChange={e => updateFaq(i, { q: e.target.value })}
                  />
                  <textarea
                    className="input w-full resize-y text-sm"
                    rows={2}
                    placeholder="Answer"
                    value={f.a}
                    onChange={e => updateFaq(i, { a: e.target.value })}
                  />
                </div>
                <RemoveButton onClick={() => patch({ faq: removeAt(form.faq, i) })} />
              </div>
            </div>
          ))}
          {form.faq.length === 0 && (
            <p className="text-[11px] italic text-iron-500">No FAQ entries yet.</p>
          )}
        </div>
        <AddRowButton
          label="Add FAQ entry"
          onClick={() => patch({ faq: [...form.faq, { q: '', a: '' }] })}
        />
      </Card>

      {/* ── Staff ── */}
      <Card>
        <SectionHeader label="Staff" />
        <div className="space-y-2">
          {form.staff.map((s, i) => (
            <div
              key={i}
              className="flex items-center gap-2 border border-iron-800/60 bg-iron-950 p-2"
            >
              <input
                type="text"
                className="input flex-1 text-sm"
                placeholder="Name"
                value={s.name}
                onChange={e => updateStaff(i, { name: e.target.value })}
              />
              <input
                type="text"
                className="input flex-1 text-sm"
                placeholder="Role (optional)"
                value={s.role}
                onChange={e => updateStaff(i, { role: e.target.value })}
              />
              <input
                type="text"
                className="input flex-1 text-sm"
                placeholder="Discord (optional)"
                value={s.discord}
                onChange={e => updateStaff(i, { discord: e.target.value })}
              />
              <RemoveButton onClick={() => patch({ staff: removeAt(form.staff, i) })} />
            </div>
          ))}
          {form.staff.length === 0 && (
            <p className="text-[11px] italic text-iron-500">No staff listed yet.</p>
          )}
        </div>
        <AddRowButton
          label="Add staff member"
          onClick={() => patch({ staff: [...form.staff, { name: '', role: '', discord: '' }] })}
        />
      </Card>

      {/* ── Featured competition ── */}
      <Card>
        <SectionHeader label="Featured competition" />
        <FieldLabel>Pin a competition to the top of your public page</FieldLabel>
        <select
          className="input w-full text-sm"
          value={form.featuredCompId}
          onChange={e => patch({ featuredCompId: e.target.value })}
        >
          <option value="">— None —</option>
          {orgComps.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.status !== 'PENDING' ? ` (${c.status.toLowerCase()})` : ''}
            </option>
          ))}
        </select>
        {orgComps.length === 0 && (
          <p className="mt-1 text-[10px] text-iron-500">No competitions in this org yet.</p>
        )}
      </Card>

      {/* ── Season Results ── */}
      <Card>
        <SectionHeader label="Season Results" />
        <p className="mb-3 text-[10px] text-iron-500">
          Manual-entry standings for past seasons. Import a competition's divisions to pull in
          ranked teams and their logos automatically, then reorder, edit scores, or remove rows
          as needed.
        </p>
        <div className="space-y-4">
          {form.seasons.map((season, si) => (
            <div key={si} className="space-y-3 border border-iron-800/60 bg-iron-950 p-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  className="input flex-1 text-sm"
                  placeholder="Season title (e.g. EGL Season 3)"
                  value={season.title}
                  onChange={e => updateSeason(si, { title: e.target.value })}
                />
                <RemoveButton onClick={() => removeSeason(si)} />
              </div>

              <SeasonImportControl
                comps={allComps}
                onImport={divisions => importDivisions(si, divisions)}
              />

              <div className="space-y-3">
                {season.divisions.map((division, di) => (
                  <div key={di} className="space-y-2 border border-iron-800/40 bg-iron-950 p-2.5">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        className="input flex-1 text-sm"
                        placeholder="Division / tier label (e.g. Challengers)"
                        value={division.tier}
                        onChange={e => updateDivision(si, di, { tier: e.target.value })}
                      />
                      <RemoveButton onClick={() => removeDivision(si, di)} />
                    </div>

                    <div className="space-y-1.5">
                      {division.teams.map((team, ti) => (
                        <div key={ti} className="flex items-center gap-2">
                          <span className="w-5 shrink-0 text-center font-mono text-[10px] text-iron-500">
                            {ti + 1}
                          </span>
                          {team.logoUrl ? (
                            <img
                              src={team.logoUrl}
                              alt=""
                              className="h-6 w-6 shrink-0 rounded bg-iron-900 object-contain"
                            />
                          ) : (
                            <span className="h-6 w-6 shrink-0" />
                          )}
                          <input
                            type="text"
                            className="input flex-1 text-sm"
                            placeholder="Clan name (canonical id)"
                            value={team.clanName}
                            onChange={e => updateTeam(si, di, ti, { clanName: e.target.value })}
                          />
                          <input
                            type="text"
                            className="input flex-1 text-sm"
                            placeholder="Display name (optional)"
                            value={team.displayName}
                            onChange={e => updateTeam(si, di, ti, { displayName: e.target.value })}
                          />
                          <input
                            type="number"
                            className="input w-20 text-right font-mono text-sm"
                            placeholder="Score"
                            value={team.score}
                            onChange={e => updateTeam(si, di, ti, { score: Number(e.target.value) || 0 })}
                          />
                          <div className="flex shrink-0 flex-col">
                            <button
                              type="button"
                              disabled={ti === 0}
                              onClick={() => moveTeam(si, di, ti, -1)}
                              className="flex h-4 w-6 items-center justify-center text-iron-500 transition-colors hover:text-forge-300 disabled:cursor-not-allowed disabled:opacity-30"
                              aria-label="Move team up"
                            >
                              <ChevronUp className="h-3 w-3" strokeWidth={2.5} />
                            </button>
                            <button
                              type="button"
                              disabled={ti === division.teams.length - 1}
                              onClick={() => moveTeam(si, di, ti, 1)}
                              className="flex h-4 w-6 items-center justify-center text-iron-500 transition-colors hover:text-forge-300 disabled:cursor-not-allowed disabled:opacity-30"
                              aria-label="Move team down"
                            >
                              <ChevronDown className="h-3 w-3" strokeWidth={2.5} />
                            </button>
                          </div>
                          <RemoveButton onClick={() => removeTeam(si, di, ti)} />
                        </div>
                      ))}
                      {division.teams.length === 0 && (
                        <p className="text-[10px] italic text-iron-500">No teams yet.</p>
                      )}
                    </div>

                    <AddRowButton label="Add team" onClick={() => addTeam(si, di)} />
                  </div>
                ))}
                {season.divisions.length === 0 && (
                  <p className="text-[11px] italic text-iron-500">
                    No divisions yet — import from a competition above, or add one manually.
                  </p>
                )}
              </div>

              <AddRowButton label="Add division" onClick={() => addDivision(si)} />
            </div>
          ))}
          {form.seasons.length === 0 && (
            <p className="text-[11px] italic text-iron-500">No seasons added yet.</p>
          )}
        </div>
        <AddRowButton label="Add season" onClick={addSeason} />
      </Card>

      {saveBar}

      </>
      )}

      </div>
      </CollapsibleSection>

      {/* ── Rules — its own segment below the Public Page dropdown; same form
             state and save path as the rest of the page blob ── */}
      <CollapsibleSection
        title="Rules"
        hint="Org rules shown on the public page for every server member — markdown."
        icon={ScrollText}
      >
        <div className="space-y-4">
          <Card>
            <SectionHeader label="Rules" />
            <div>
              <FieldLabel>Org rules (markdown)</FieldLabel>
              <textarea
                className="input w-full resize-y font-mono text-sm"
                rows={10}
                placeholder={'# Code of conduct\n1. Be decent.\n2. No smurfing.\n\n## Match rules\n- Check in 15 min before start…'}
                value={form.rules}
                onChange={e => patch({ rules: e.target.value })}
              />
              <p className="mt-1 text-xs text-ink-meta">
                Supports headings (#, ##), numbered and bulleted lists, and paragraphs. Shown on the org page for every server member.
              </p>
            </div>
          </Card>
          {saveBar}
        </div>
      </CollapsibleSection>
    </div>
  );
}
